import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../API.js';
import UserPicker from '../../components/UserPicker';
import styles from './UsersManagement.module.css';

function normalizeUser(u) {
    if (!u) return null;
    return {
        _id: u._id,
        login: u.login || '',
        email: u.email || '',
        firstName: u.firstName || '',
        lastName: u.lastName || '',
    };
}

export default function EmailauthDelegationDialog({ open, onClose }) {
    const dialogRef = useRef(null);
    const closeButtonRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [delegates, setDelegates] = useState([]);

    const [selectedUser, setSelectedUser] = useState(null);
    const [grantPending, setGrantPending] = useState(false);
    const [revokePendingById, setRevokePendingById] = useState(() => new Map());

    const delegateIds = useMemo(() => new Set(delegates.map((d) => String(d._id))), [delegates]);
    const selectedUserId = String(selectedUser?._id || '');
    const selectedAlreadyDelegated = !!selectedUserId && delegateIds.has(selectedUserId);
    const selectedLabel = useMemo(() => {
        if (!selectedUser) return '';
        const login = selectedUser.login || '';
        const name = [selectedUser.firstName, selectedUser.lastName].filter(Boolean).join(' ').trim();
        const email = selectedUser.email || '';
        const parts = [login, name, email].filter(Boolean);
        return parts.join(' — ');
    }, [selectedUser]);

    const loadDelegates = useCallback(() => {
        setLoading(true);
        setError('');
        return api.get('emailauth/delegates')
            .then((resp) => {
                if (resp.status !== 200 || !resp.data) {
                    setDelegates([]);
                    setError('Could not load delegated users.');
                    return;
                }
                const raw = Array.isArray(resp.data.delegates) ? resp.data.delegates : [];
                setDelegates(raw.map(normalizeUser).filter(Boolean));
            })
            .catch(() => {
                setDelegates([]);
                setError('Could not load delegated users.');
            })
            .finally(() => setLoading(false));
    }, []);

    const close = useCallback(() => {
        setMessage('');
        setError('');
        setSelectedUser(null);
        onClose?.();
    }, [onClose]);

    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        loadDelegates().finally(() => {
            if (cancelled) return;
            requestAnimationFrame(() => {
                closeButtonRef.current?.focus();
            });
        });
        return () => { cancelled = true; };
    }, [open, loadDelegates]);

    if (!open) return null;

    async function grant() {
        const userId = selectedUser?._id;
        if (!userId) return;
        if (delegateIds.has(String(userId))) {
            setMessage('That user already has delegated permission.');
            return;
        }
        setGrantPending(true);
        setMessage('');
        setError('');
        try {
            const resp = await api.post(`emailauth/delegates/${userId}/grant`);
            if (resp.status !== 200) {
                setError('Could not grant delegation.');
                return;
            }
            setMessage('Delegation granted.');
            setSelectedUser(null);
            await loadDelegates();
        } catch {
            setError('Could not grant delegation.');
        } finally {
            setGrantPending(false);
        }
    }

    async function revoke(userId) {
        if (!userId) return;
        setMessage('');
        setError('');
        setRevokePendingById((prev) => {
            const next = new Map(prev);
            next.set(String(userId), true);
            return next;
        });
        try {
            const resp = await api.post(`emailauth/delegates/${userId}/revoke`);
            if (resp.status !== 200) {
                setError('Could not revoke delegation.');
                return;
            }
            setMessage('Delegation revoked.');
            await loadDelegates();
        } catch {
            setError('Could not revoke delegation.');
        } finally {
            setRevokePendingById((prev) => {
                const next = new Map(prev);
                next.delete(String(userId));
                return next;
            });
        }
    }

    return (
        <div
            className={styles.overlay}
            role="presentation"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) close();
            }}
        >
            <div
                className={styles.dialog}
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="admin-users-delegation-title"
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div className={styles.dialogTitleRow}>
                    <h2 className={styles.dialogTitle} id="admin-users-delegation-title">
                        Delegated passwordless user creation
                    </h2>
                </div>

                <div style={{ display: 'grid', gap: 14 }}>
                    <section>
                        <h3 style={{ margin: '0 0 8px' }}>Grant delegation</h3>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                            <div style={{ minWidth: 320, flex: '1 1 320px' }}>
                                <UserPicker
                                    onSelect={(u) => setSelectedUser(u)}
                                    placeholder="Search a user…"
                                    excludeIds={[]}
                                    showEmail
                                />
                            </div>
                            <button
                                type="button"
                                className={styles.primaryButton}
                                disabled={!selectedUser?._id || selectedAlreadyDelegated || grantPending}
                                onClick={grant}
                            >
                                {grantPending ? 'Granting…' : (selectedUser?.login ? `Grant to ${selectedUser.login}` : 'Grant')}
                            </button>
                        </div>
                        {selectedUser?._id ? (
                            <div className={styles.selectedRow} aria-live="polite">
                                <div>
                                    <span className={styles.muted}>Selected:</span>{' '}
                                    <strong>{selectedLabel || selectedUser._id}</strong>
                                    {selectedAlreadyDelegated ? (
                                        <span className={styles.muted}> (already delegated)</span>
                                    ) : null}
                                </div>
                                <button
                                    type="button"
                                    className={styles.secondaryButton}
                                    onClick={() => setSelectedUser(null)}
                                    disabled={grantPending}
                                >
                                    Clear
                                </button>
                            </div>
                        ) : null}
                        <p className={styles.muted} style={{ marginTop: 8 }}>
                            Delegated users can create passwordless accounts via emailauth.
                        </p>
                    </section>

                    <section>
                        <h3 style={{ margin: '0 0 8px' }}>Delegated users</h3>
                        {loading ? <p aria-live="polite">Loading…</p> : null}
                        {error ? <p role="alert">{error}</p> : null}
                        {message ? <p aria-live="polite">{message}</p> : null}

                        <div className={styles.tableWrap} style={{ marginTop: 8 }}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Login</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {delegates.map((u) => {
                                        const pending = revokePendingById.get(String(u._id));
                                        return (
                                            <tr key={u._id} className={styles.row}>
                                                <td>{u.firstName} {u.lastName}</td>
                                                <td>{u.email}</td>
                                                <td>{u.login}</td>
                                                <td style={{ textAlign: 'right' }}>
                                                    <button
                                                        type="button"
                                                        className={styles.secondaryButton}
                                                        disabled={!!pending}
                                                        onClick={() => revoke(u._id)}
                                                    >
                                                        {pending ? 'Revoking…' : 'Revoke'}
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {!loading && delegates.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className={styles.muted}>
                                                No delegated users.
                                            </td>
                                        </tr>
                                    ) : null}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </div>

                <div className={styles.dialogFooter}>
                    <button
                        type="button"
                        className={styles.secondaryButton}
                        onClick={close}
                        ref={closeButtonRef}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

